/**
 * Utility functions for creating drag clones in booking cards
 * Centralizes drag clone creation logic to avoid duplication
 */

import { ViewBooking } from "collins-js-sdk/models/ViewBooking";

export const TOUCH_DRAG_MODE_EVENT = "collins-touch-drag-mode";
export const TOUCH_DRAG_HOVER_EVENT = "collins-touch-drag-hover";
export const TOUCH_DRAG_DROP_EVENT = "collins-touch-drag-drop";

type TouchDragModeEventDetail = {
  active: boolean;
};

type TouchDragHoverEventDetail = {
  tableId: string | null;
};

type TouchDragDropEventDetail = {
  bookingData: string;
  clientX: number;
  clientY: number;
};

type TouchDragAnchor = {
  offsetX: number;
  offsetY: number;
};

let currentTouchHoverTableId: string | null = null;

/**
 * Creates a drag clone element with optimized styling for performance
 * @param bookingId - The ID of the booking to clone
 * @returns The cloned element or null if source not found
 */
export const createDragClone = (bookingId: string): HTMLElement | null => {
  const getCard = document.getElementById(`Booking_${bookingId}`);
  if (!getCard) return null;

  const clone = getCard.cloneNode(true) as HTMLElement;
  document.body.appendChild(clone);

  // Style the drag image for better visibility and performance
  clone.style.setProperty("width", "320px", "important");
  clone.style.setProperty("position", "absolute", "important");
  clone.style.setProperty("top", "-9999px", "important");
  clone.style.setProperty("left", "-9999px", "important");
  clone.style.setProperty("pointer-events", "none", "important");

  // GPU acceleration for smooth dragging on touch devices
  clone.style.setProperty("transform", "translateZ(0)", "important");
  clone.style.setProperty("will-change", "transform", "important");

  clone.classList.add("draggedBookingType");

  return clone;
};

/**
 * Calculates the drag offset for touch devices
 * @param clone - The cloned element
 * @returns The x-offset for the drag image on touch devices
 */
export const calculateTouchDragOffset = (clone: HTMLElement): number => {
  return -Math.abs((clone.getBoundingClientRect().width - 20) / 2);
};

/**
 * Calculates the drag offset for desktop devices
 * @param clone - The cloned element
 * @returns The x-offset for the drag image on desktop devices
 */
export const calculateDesktopDragOffset = (clone: HTMLElement): number => {
  return clone.getBoundingClientRect().width / 2;
};

/**
 * Cleans up all drag clone elements from the DOM
 */
export const cleanupDragClones = (): void => {
  const allDraggedElements =
    document.getElementsByClassName("draggedBookingType");

  // Convert HTMLCollection to Array for proper for-of iteration
  for (const element of Array.from(allDraggedElements)) {
    element.remove();
  }
};

/**
 * Prepares an existing drag clone for touch dragging so it follows the finger.
 */
export const prepareTouchDragImage = (
  clone: HTMLElement,
  clientX: number,
  clientY: number,
  touchAnchor?: TouchDragAnchor
): void => {
  const cachedWidth = clone.getBoundingClientRect().width || 320;

  clone.style.setProperty("position", "fixed", "important");
  clone.style.setProperty("top", "0", "important");
  clone.style.setProperty("left", "0", "important");
  clone.style.setProperty("z-index", "9999", "important");
  clone.style.setProperty("opacity", "0.92", "important");
  clone.style.setProperty("transition", "none", "important");
  clone.style.setProperty("animation", "none", "important");
  clone.style.setProperty("backface-visibility", "hidden", "important");
  clone.style.setProperty("-webkit-backface-visibility", "hidden", "important");
  clone.style.setProperty("will-change", "transform", "important");
  clone.style.setProperty("pointer-events", "none", "important");
  clone.setAttribute("data-touch-drag-width", String(cachedWidth));
  if (
    touchAnchor &&
    Number.isFinite(touchAnchor.offsetX) &&
    Number.isFinite(touchAnchor.offsetY)
  ) {
    const clampedOffsetX = Math.min(
      Math.max(touchAnchor.offsetX, 0),
      cachedWidth
    );
    const clampedOffsetY = Math.max(touchAnchor.offsetY, 0);

    clone.setAttribute("data-touch-drag-offset-x", String(clampedOffsetX));
    clone.setAttribute("data-touch-drag-offset-y", String(clampedOffsetY));
  } else {
    clone.removeAttribute("data-touch-drag-offset-x");
    clone.removeAttribute("data-touch-drag-offset-y");
  }
  clone.style.setProperty(
    "transform",
    "translate(-9999px, -9999px)",
    "important"
  );
  moveTouchDragImage(clone, clientX, clientY);
};

/**
 * Moves the touch drag image to follow the pointer.
 */
export const moveTouchDragImage = (
  clone: HTMLElement,
  clientX: number,
  clientY: number
): void => {
  const cachedWidthAttr = clone.getAttribute("data-touch-drag-width");
  const cachedWidth =
    cachedWidthAttr === null ? Number.NaN : Number(cachedWidthAttr);
  const width =
    Number.isFinite(cachedWidth) && cachedWidth > 0 ? cachedWidth : 320;
  const cachedOffsetXAttr = clone.getAttribute("data-touch-drag-offset-x");
  const cachedOffsetYAttr = clone.getAttribute("data-touch-drag-offset-y");
  const cachedOffsetX =
    cachedOffsetXAttr === null ? Number.NaN : Number(cachedOffsetXAttr);
  const cachedOffsetY =
    cachedOffsetYAttr === null ? Number.NaN : Number(cachedOffsetYAttr);
  const offsetX =
    Number.isFinite(cachedOffsetX) && cachedOffsetX >= 0
      ? cachedOffsetX
      : width / 2;
  const offsetY =
    Number.isFinite(cachedOffsetY) && cachedOffsetY >= 0 ? cachedOffsetY : 20;

  clone.style.setProperty(
    "transform",
    `translate3d(${clientX - offsetX}px, ${clientY - offsetY}px, 0)`,
    "important"
  );
};

/**
 * Broadcasts touch drag mode state so floorplan can switch drag visuals.
 */
export const dispatchTouchDragModeEvent = (active: boolean): void => {
  window.dispatchEvent(
    new CustomEvent<TouchDragModeEventDetail>(TOUCH_DRAG_MODE_EVENT, {
      detail: { active }
    })
  );
};

/**
 * Broadcasts the currently hovered table id while touch-dragging.
 */
export const dispatchTouchDragHoverEvent = (tableId: string | null): void => {
  currentTouchHoverTableId = tableId;

  window.dispatchEvent(
    new CustomEvent<TouchDragHoverEventDetail>(TOUCH_DRAG_HOVER_EVENT, {
      detail: { tableId }
    })
  );
};

const getDropTargetTableById = (tableId: string | null): HTMLElement | null => {
  if (!tableId) {
    return null;
  }

  const tableElements =
    document.querySelectorAll<HTMLElement>("[data-table-id]");

  for (const element of Array.from(tableElements)) {
    if (element.getAttribute("data-table-id") === tableId) {
      return element;
    }
  }

  return null;
};

const getDropTargetTableElement = (
  clientX: number,
  clientY: number
): HTMLElement | null => {
  const target = document.elementFromPoint(clientX, clientY);
  if (!target) {
    return null;
  }

  return target.closest("[data-table-id]");
};

/**
 * Calculates and broadcasts hovered table from touch position.
 */
export const dispatchTouchDragHoverFromPoint = (
  clientX: number,
  clientY: number
): void => {
  const targetTable = getDropTargetTableElement(clientX, clientY);
  const tableId = targetTable?.getAttribute("data-table-id") ?? null;
  dispatchTouchDragHoverEvent(tableId);
};

/**
 * Dispatches custom touch drop event directly to the table element.
 */
export const dispatchTouchDropEvent = (
  booking: ViewBooking,
  clientX: number,
  clientY: number
): boolean => {
  const targetTable =
    getDropTargetTableElement(clientX, clientY) ??
    getDropTargetTableById(currentTouchHoverTableId);

  if (!targetTable) {
    return false;
  }

  targetTable.dispatchEvent(
    new CustomEvent<TouchDragDropEventDetail>(TOUCH_DRAG_DROP_EVENT, {
      bubbles: true,
      detail: {
        bookingData: JSON.stringify(booking),
        clientX,
        clientY
      }
    })
  );

  return true;
};
